import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";
import {
	fetchPersonInmailCostData,
	type InmailCostEntry,
	type InmailCostQueueItem,
} from "../../api/analytics/inmail-cost-queue";
import { formatDateTimeWithSeconds as formatDateTime } from "../../utils/date-formatting";
import { useGlobalState } from "../globalState";

interface PersonInmailCostStatusProps {
	personId: number;
}

export const PersonInmailCostStatus = ({ personId }: PersonInmailCostStatusProps) => {
	const { currentUser } = useGlobalState();
	const isSuperuser = !!currentUser?.is_superuser;

	const [queueItems, setQueueItems] = useState<InmailCostQueueItem[]>([]);
	const [costResults, setCostResults] = useState<InmailCostEntry[]>([]);
	const [loading, setLoading] = useState<boolean>(true);

	useEffect(() => {
		if (!isSuperuser || !personId) return;

		setLoading(true);
		fetchPersonInmailCostData(personId)
			.then((data) => {
				setQueueItems(data.queue_items || []);
				setCostResults(data.cost_results || []);
			})
			.catch((error) => {
				console.error("Failed to fetch InMail cost data:", error);
			})
			.finally(() => {
				setLoading(false);
			});
	}, [isSuperuser, personId]);

	if (!isSuperuser) return null;

	const getCostDisplay = (
		cost: number
	): { label: string; color: "success" | "error" | "warning" | "default" } => {
		if (cost === 0) return { label: "FREE", color: "success" };
		if (cost === 1) return { label: "PAID", color: "error" };
		if (cost >= 400) return { label: `ERROR (${cost})`, color: "warning" };
		return { label: String(cost), color: "default" };
	};

	return (
		<Grid size={12} data-component="person-inmail-cost-status">
			<Accordion data-component="person-inmail-cost-accordion">
				<AccordionSummary
					expandIcon={<ExpandMoreIcon />}
					data-component="person-inmail-cost-accordion-summary"
				>
					<Stack direction="row" spacing={2} alignItems="center">
						<Typography
							variant="h6"
							data-component="person-inmail-cost-title"
						>
							InMail Cost Status
						</Typography>
						<Tooltip title="Open InMail Cost Queue page (filtered by this person)">
							<IconButton
								size="small"
								href={`/analytics/inmail-cost-queue?person_id=${personId}`}
								target="_blank"
								onClick={(e) => e.stopPropagation()}
								data-component="inmail-cost-queue-link"
							>
								<OpenInNewIcon fontSize="small" />
							</IconButton>
						</Tooltip>
						{queueItems.length > 0 && (
							<Chip
								label={`${queueItems.length} queued`}
								size="small"
								color="info"
								data-component="queue-count-chip"
							/>
						)}
						{costResults.length > 0 && (
							<Chip
								label={`${costResults.length} results`}
								size="small"
								color="primary"
								data-component="results-count-chip"
							/>
						)}
					</Stack>
				</AccordionSummary>
				<AccordionDetails data-component="person-inmail-cost-accordion-details">
					<Card data-component="person-inmail-cost-card">
						<CardContent data-component="person-inmail-cost-card-content">
							{loading ? (
								<Typography variant="body2" color="text.secondary">
									Loading...
								</Typography>
							) : (
								<Stack spacing={3}>
									{/* Queue Items Section */}
									<Box data-component="queue-items-section">
										<Typography variant="subtitle1" gutterBottom>
											Queue Items (Pending Cost Checks)
										</Typography>
										{queueItems.length > 0 ? (
											<Table
												size="small"
												data-component="queue-items-table"
											>
												<TableHead>
													<TableRow>
														<TableCell>Priority</TableCell>
														<TableCell>Bot</TableCell>
														<TableCell>Type</TableCell>
														<TableCell>Created</TableCell>
													</TableRow>
												</TableHead>
												<TableBody>
													{queueItems.map((item) => (
														<TableRow key={item.id} hover>
															<TableCell>
																{item.priority}
															</TableCell>
															<TableCell>
																{item.bot_name}
															</TableCell>
															<TableCell>
																{item.type}
															</TableCell>
															<TableCell>
																{formatDateTime(
																	item.created
																)}
															</TableCell>
														</TableRow>
													))}
												</TableBody>
											</Table>
										) : (
											<Typography
												variant="body2"
												color="text.secondary"
											>
												No pending queue items
											</Typography>
										)}
									</Box>

									{/* Cost Results Section */}
									<Box data-component="cost-results-section">
										<Typography variant="subtitle1" gutterBottom>
											Cost Check Results
										</Typography>
										{costResults.length > 0 ? (
											<Table
												size="small"
												data-component="cost-results-table"
											>
												<TableHead>
													<TableRow>
														<TableCell>User</TableCell>
														<TableCell>Cost</TableCell>
														<TableCell>
															Checked At
														</TableCell>
													</TableRow>
												</TableHead>
												<TableBody>
													{costResults.map((cost) => {
														const display = getCostDisplay(
															cost.cost
														);
														return (
															<TableRow
																key={cost.id}
																hover
															>
																<TableCell>
																	{cost.user_username}
																</TableCell>
																<TableCell>
																	<Chip
																		label={
																			display.label
																		}
																		size="small"
																		color={
																			display.color
																		}
																		data-component="cost-chip"
																	/>
																</TableCell>
																<TableCell>
																	{formatDateTime(
																		cost.created
																	)}
																</TableCell>
															</TableRow>
														);
													})}
												</TableBody>
											</Table>
										) : (
											<Typography
												variant="body2"
												color="text.secondary"
											>
												No cost check results
											</Typography>
										)}
									</Box>
								</Stack>
							)}
						</CardContent>
					</Card>
				</AccordionDetails>
			</Accordion>
		</Grid>
	);
};

export default PersonInmailCostStatus;
